import React, { useEffect, useMemo, useState } from 'react';
import { getFormattedAmount } from '@zonofi/common';
import {
  getCustomerInvite,
  useAppSelector,
  useGetAdminAccountsMutation,
  useGetOrderDetailsMutation,
  useGetTrackPODetailsMutation,
} from '@zonofi/zono-money-store';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const usePaymentAdvice = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { orderId } = params;
  const [searchParams] = useSearchParams();
  const cart = searchParams.get('cart') === 'true';
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, inviteId } = inviteDetails;
  const [fetchOrder, fetchOrderResponse] = useGetOrderDetailsMutation();
  const [fetchPoFile, fetchPoFileResponse] = useGetTrackPODetailsMutation();
  const [getAccountsAPI, getAccountsAPIResponse] =
    useGetAdminAccountsMutation();
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const [bankAccountDetails, setBankAccountDetails] = useState<
    { label: string; value: string }[]
  >([]);
  const [upiDetails, setUpiDetails] = useState<{
    name: string;
    id: string;
    qrUrl: string;
  }>({
    name: '',
    id: '',
    qrUrl: '',
  });
  const isPoFile = useMemo(() => {
    return isNaN(Number(orderId));
  }, [orderId]);
  const isLoading = useMemo(() => {
    return (
      fetchOrderResponse?.isLoading ||
      fetchPoFileResponse?.isLoading ||
      getAccountsAPIResponse?.isLoading
    );
  }, [fetchOrderResponse, fetchPoFileResponse, getAccountsAPIResponse]);
  useEffect(() => {
    if (!orderId) return;
    if (isPoFile) {
      fetchPoFile({
        workspaceId: seller?.workspaceId,
        body: {
          includeInvoice: true,
          includeTax: true,
          pofileId: String(orderId),
          sortBy: 'sequence',
          sortDirection: 'ASC',
          customerId: inviteId,
          includePromotionData: true,
        },
      });
    } else {
      fetchOrder({
        workspaceId: seller?.workspaceId,
        orderId: Number(orderId),
        body: {
          includeInvoice: true,
          includeTax: true,
          includeCustomer: true,
          includePromotions: true,
          customerId: inviteId,
          includePromotionData: true,
        },
      });
    }
  }, [isPoFile, orderId]);

  const {
    advanceAmount,
    lineOrderId,
    totalItems,
    refOrderNumber,
    status,
    customerName,
  } = useMemo(() => {
    if (isPoFile) {
      return {
        advanceAmount: fetchPoFileResponse?.data?.advancePaymentAmount
          ? getFormattedAmount(
              parseFloat(fetchPoFileResponse?.data?.advancePaymentAmount)
            )
          : '-',
        lineOrderId: fetchPoFileResponse?.data?.lines?.find((x) => x.orderId)
          ?.orderId,
        totalItems: fetchPoFileResponse?.data?.totalQuantity,
        refOrderNumber: fetchPoFileResponse?.data?.refOrderNumber,
        status: fetchPoFileResponse?.data?.status,
        customerName: fetchPoFileResponse?.data?.customerName,
      };
    } else {
      return {
        advanceAmount: fetchOrderResponse?.data?.advancePaymentAmount
          ? getFormattedAmount(
              parseFloat(fetchOrderResponse?.data?.advancePaymentAmount)
            )
          : '-',
        lineOrderId: fetchOrderResponse?.data?.lines?.find((x) => x.orderId)
          ?.orderId,
        totalItems: fetchOrderResponse?.data?.totalQuantity,
        refOrderNumber: fetchOrderResponse?.data?.refOrderNumber,
        status: fetchOrderResponse?.data?.status,
        customerName: fetchOrderResponse?.data?.distributorName,
      };
    }
  }, [isPoFile, fetchPoFileResponse, fetchOrderResponse]);

  const onCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedLabel(label);
    setTimeout(() => {
      setCopiedLabel(null);
    }, 1000);
  };
  const onBackBtnClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    getAccountsAPI({
      params: {
        sellerWorkspaceId: seller?.workspaceId,
      },
      body: {
        workspaceId: seller?.workspaceId,
        paymentType: 'GeneratePaymentNotification',
        inviteId: inviteId,
      },
    });
  }, []);

  useEffect(() => {
    if (getAccountsAPIResponse?.data) {
      const account =
        getAccountsAPIResponse?.data?.bankAccounts?.find(
          (x) => x?.accountNumber
        ) || {};
      const data = [];
      Object.entries(account).forEach(([key, value]) => {
        if (
          (key === 'bankName' ||
            key === 'accountNumber' ||
            key === 'accountHolderName' ||
            key === 'ifscCode') &&
          value
        ) {
          data.push({ label: key, value: value });
        }
      });
      setBankAccountDetails(data);
      const upiData = getAccountsAPIResponse?.data?.upiAccounts?.find(
        (x) => x?.qrCodeUrl && x?.upiId
      );
      setUpiDetails({
        name: upiData?.upiAccountHolderName || '',
        id: upiData?.upiId || '',
        qrUrl: upiData?.qrCodeUrl,
      });
    }
  }, [getAccountsAPIResponse]);
  return {
    advanceAmount,
    lineOrderId,
    totalItems,
    sellerName: seller?.name,
    isLoading,
    onBackBtnClick,
    onCopy,
    copiedLabel,
    bankAccountDetails,
    upiDetails,
    orderId,
    refOrderNumber,
    status,
    customerName,
    cart,
  };
};
